import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { observer } from 'mobx-react';

import usePublicApi from 'public-api';
import { isResponse } from 'public-api/utils';
import { useAsyncFunction } from 'stores/hooks';
import { Card, Loader, Overlay } from 'ui';

import Communication from './Communication';
import Header from './Header';
import Products from './Products';
import Remarketing from './Remarketing';
import Shipping from './Shipping';
import Transport from './Transport';

type RouteParams = {
  ticketId: string;
};

function TicketDetail() {
  const { ticketId } = useParams<RouteParams>();

  const [ticket, setTicket] = useState<null | BackendPublic.TicketDetail>(null);
  const [ticketNotFound, setTicketNotFound] = useState(false);
  const [steps, setSteps] = useState<null | BackendPublic.TicketDetail['process_steps']>(null);
  const [products, setProducts] = useState<null | BackendPublic.Products[]>(null);
  const api = usePublicApi();

  const loadTicketOnly = async () => {
    try {
      const data = await api.ClientTicketApi.retrieve({ id: ticketId! });
      setTicket(data);
      setSteps(data.process_steps);
    } catch (e) {
      if (isResponse(e) && e.response.status === 404) {
        setTicketNotFound(true);
      }
    }
  };

  const [handleLoadTicketOnly] = useAsyncFunction(loadTicketOnly);

  const load = async () => {
    await handleLoadTicketOnly();
    const response = await api.ClientTicketProductsApi.list({ id: ticketId! });
    setProducts(response);
  };

  const [handleLoad, isLoading] = useAsyncFunction(load);

  useEffect(() => {
    handleLoad();
  }, []);

  if (ticketNotFound) {
    return (
      <Card>
        <Card.Content>
          <h3 className="text-center text-2xl font-medium">{gettext('Ticket not found')}</h3>
        </Card.Content>
      </Card>
    );
  }

  return ticket !== null && products !== null && steps !== null && !isLoading ? (
    <div className="space-y-8">
      <Header ticket={ticket} steps={steps} />
      <Products ticket={ticket} products={products} />
      <Transport ticket={ticket} />
      <Shipping ticket={ticket} />
      <Remarketing ticket={ticket} />
      <Communication ticket={ticket} onSend={loadTicketOnly} />
    </div>
  ) : (
    <Overlay inverted open onOverlayClick={() => null}>
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform">
        <Loader size="huge" />
      </div>
    </Overlay>
  );
}

export default observer(TicketDetail);
